import {
    FGIcons,
    FGStack,
    FGStackItem,
    FGTile,
    FGTypography,
} from '@fg/design-system';
import { find, flow, forEach, get, map, size } from 'lodash/fp';
import React, { ReactNode, useCallback, useRef } from 'react';
import EventsFeedDataProvider from 'components/eventsFeed/EventFeedDataProvider';
import { useNavigate } from 'react-router-dom';
import {
    IActiveCountry,
    IActiveCountryMap,
    IActiveGroup,
    VIEW,
} from 'interfaces';
import cx from 'classnames';
import { useCurrentView } from 'hooks/useCurrentView';
import { inject } from 'mobx-react';
import NewCountryPicker, { NewCountryPickerFill } from 'components/NewCountryPicker/NewCountryPicker';
import rootStore, { IMobxRootState } from 'RootStore';
import PickerToggler, { PICKER_GAP } from './PickerToggler';
import { MultiCountryPickerPreview } from './MultiCountryPickerPreview';
import SingleRiskPickerPreview from './SingleRiskPickerPreview';
import DatePickerContainer from './DatePickerContainer';
import { RiskPickerIndicators } from 'components/RiskPicker/RiskPickerIndicators';
import { PickerDrawer } from './PickerDrawer';
import { isEmpty } from 'lodash';
import { usePopupTopOffset } from 'hooks/usePopupTopOffset';
import HeatmapRiskSelection from 'components/Heatmap/HeatmapRiskSelection';
import { useViewUrlSync } from 'services/urlState/useViewUrlSync';
import { compareCountriesSchema } from 'services/urlState/viewSchemas';

type Props = {
    licensedCountriesArr?: IActiveCountry[];
    selectedCountries?: number[];
    groups?: IActiveGroup[];
    activeGroupId?: string;
    countries?: IActiveCountryMap;
    heatmapCountries?: number[];
};

export const CROSS_COUNTRY_VIEWS = [
    { path: VIEW.WORLD_GRAPH, label: 'Line Graph', icon: 'Curves' },
    { path: VIEW.SORT_BY_RISK, label: 'Sort By Rank', icon: 'Dots' },
    { path: VIEW.SORT_BY_DELTA, label: 'Sort By Delta', icon: 'Triangle' },
    { path: VIEW.HEATMAP, label: 'Heatmap', icon: 'Fire' },
];
export const CrossCountryRiskPicker = inject(
    ({ countryStore, settingsStore, heatmapStore }: IMobxRootState) => {
        return {
            licensedCountriesArr: countryStore.countries.licensedCountriesArr,
            selectedCountries: countryStore.currentCountryList,
            groups: settingsStore.activeGroups,
            activeGroupId: settingsStore.activeGroupId,
            countries: countryStore.countries.countries,
            heatmapCountries: heatmapStore.countriesList,
        };
    }
)((props: Props) => {
    const {
        licensedCountriesArr,
        groups,
        activeGroupId,
        selectedCountries,
        heatmapCountries,
    } = props;

    const heatmapSelectedCountries = licensedCountriesArr
        .filter(country => heatmapCountries.includes(country.id))
        .map(country => country.id);
    const isAllHeatmapSelected =
        size(heatmapSelectedCountries) === size(licensedCountriesArr);
    const navigate = useNavigate();
    const currentView = useCurrentView();
    const isHeatmap = currentView === VIEW.HEATMAP;
    const riskPickerRef = useRef(null)
    const [countryTogglerRef, countryPopupTop] = usePopupTopOffset();

    const renderHeatmapCountryPicker = useCallback(
        () => (
            <NewCountryPicker
                canSelectAllCountries={true}
                countriesList={licensedCountriesArr}
                selectedCountriesList={licensedCountriesArr
                    .filter(country => heatmapCountries.includes(country.id))
                    .map(country => country.id)}
                groups={groups}
                isMultiSelect
                //todo: check if this needs to be different for heatmap or not
                //TODO: For Eilam - looks like this callback is not used.
                // Inside NewCountryPicker it calls a local `onGroupClicked` function, and not props.onGroupClicked
                onGroupClicked={(id: string) => {
                    rootStore.settingsStore.setActiveGroupId(id);
                    const activeGroup = find(e => e.id == id, groups);
                    rootStore.heatmapStore.setCountriesList(
                        activeGroup.countries
                    );
                }}
                activeGroupId={activeGroupId}
                onCountrySelected={(countryId: number) =>
                    rootStore.heatmapStore.toggleCountry(countryId)
                }
                onAllSelected={(id: string) => {
                    if (!id) {
                        rootStore.heatmapStore.setCountriesList(
                            map(get('id'), licensedCountriesArr)
                        );
                        return;
                    }
                    const activeGroupCountries: number[] =
                        flow(
                            find((group: IActiveGroup) => group.id == id),
                            get('countries')
                        )(groups) || [];
                    const merged = Array.from(
                        new Set([
                            ...rootStore.heatmapStore.countriesList,
                            ...activeGroupCountries,
                        ])
                    );
                    rootStore.heatmapStore.setCountriesList(merged);
                }}
                onAllUnselected={(id: string) => {
                    if (!id) {
                        rootStore.heatmapStore.setCountriesList([]);
                        return;
                    }
                    const activeGroupCountries: number[] =
                        flow(
                            find((group: IActiveGroup) => group.id == id),
                            get('countries')
                        )(groups) || [];
                    forEach(
                        countryId =>
                            rootStore.heatmapStore.removeCountry(countryId),
                        activeGroupCountries
                    );
                }}
            />
        ),
        [licensedCountriesArr, heatmapCountries, groups, activeGroupId]
    );

    const renderNonHeatmapCountryPicker = useCallback(() => {
        return (
            <NewCountryPicker
                canSelectAllCountries={true}
                countriesList={licensedCountriesArr}
                selectedCountriesList={selectedCountries}
                groups={groups}
                isMultiSelect={true}
                onCountrySelected={(countryId: number) => {
                    rootStore.countryStore.toggleCountry(countryId);
                }}
                onAllUnselected={(id: string) => {
                    //todo: refactor activeGroupCountries into a function
                    const activeGroupCountries = flow(
                        find((group: IActiveGroup) => group.id == id),
                        get('countries')
                    )(groups);
                    forEach(
                        countryId =>
                            rootStore.countryStore.removeCountry(countryId),
                        activeGroupCountries
                    );
                }}
                onAllSelected={id => {
                    const activeGroupCountries = flow(
                        find((group: IActiveGroup) => group.id == id),
                        get('countries')
                    )(groups);
                    forEach(
                        countryId =>
                            rootStore.countryStore.addCountry(countryId),
                        activeGroupCountries
                    );
                }}
            />
        );
    }, [licensedCountriesArr, selectedCountries, groups]);
    return (
        <FGStack direction="column" style={{ width: '100%', height: '100%' }}>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Countries
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <div ref={countryTogglerRef}>
                            <PickerToggler
                                alignItems={
                                    isHeatmap
                                        ? 'center'
                                        : !isEmpty(selectedCountries)
                                        ? 'flex-start'
                                        : 'center'
                                }
                                labelComponent={
                                    isHeatmap ? (
                                        <FGTypography
                                            tag="label"
                                            variant="label3">
                                            {isAllHeatmapSelected
                                                ? `All countries (${size(
                                                      licensedCountriesArr
                                                  )})`
                                                : `${size(
                                                      heatmapSelectedCountries
                                                  )} countries`}
                                        </FGTypography>
                                    ) : (
                                        <MultiCountryPickerPreview
                                            selectedCountries={
                                                selectedCountries
                                            }
                                            onCountryClick={countryId => {
                                                rootStore.countryStore.toggleCountry(
                                                    countryId
                                                );
                                            }}
                                        />
                                    )
                                }
                                icon="Navigator">
                                <NewCountryPickerFill top={countryPopupTop}>
                                    {isHeatmap
                                        ? renderHeatmapCountryPicker()
                                        : renderNonHeatmapCountryPicker()}
                                </NewCountryPickerFill>
                            </PickerToggler>
                        </div>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Feature
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <FGStack gap={8} direction="column">
                            {map(
                                view => (
                                    <FGTile
                                        key={view.path}
                                        padding="small"
                                        className={cx('button', {
                                            active: view.path === currentView,
                                        })}
                                        appearance="secondary"
                                        onClick={() =>
                                            navigate(`/${view.path}`)
                                        }>
                                        <FGStack gap={8}>
                                            {/* @ts-ignore*/}
                                            <FGIcons icon={view.icon} />
                                            <FGTypography
                                                tag="label"
                                                variant="label2">
                                                {view.label}
                                            </FGTypography>
                                        </FGStack>
                                    </FGTile>
                                ),
                                CROSS_COUNTRY_VIEWS
                            )}
                        </FGStack>
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
            <FGStackItem>
                <FGStack gap={PICKER_GAP} direction="column">
                    <FGStackItem>
                        <FGTypography
                            tag="label"
                            variant="label2"
                            color="strong">
                            Date
                        </FGTypography>
                    </FGStackItem>
                    <FGStackItem>
                        <DatePickerContainer currentView={currentView} />
                    </FGStackItem>
                </FGStack>
            </FGStackItem>
                <FGStackItem>
                    <FGStack gap={PICKER_GAP} direction="column">
                        <FGStackItem>
                            <FGTypography
                                tag="label"
                                variant="label2"
                                color="strong">
                                Risk Type
                            </FGTypography>
                        </FGStackItem>
                        <FGStackItem>
                            {isHeatmap ? <HeatmapRiskSelection/> : <PickerToggler
                                ref={riskPickerRef}
                                labelComponent={<SingleRiskPickerPreview />}
                                icon="Charts"
                                collapsedIcon="RightArrow">
                                <div
                                    style={{
                                        //todo: use Absolute but fix width: 100%;
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                    }}>
                                    <RiskPickerIndicators
                                        onClose={() =>
                                            riskPickerRef.current.close()
                                        }
                                    />
                                </div>
                            </PickerToggler>}
                        </FGStackItem>
                    </FGStack>
                </FGStackItem>
        </FGStack>
    );
});

type CrossCountryRiskPageProps = {
    children: ReactNode;
};

export const CrossCountryRiskPage = React.memo(
    (props: CrossCountryRiskPageProps) => {
        useViewUrlSync(compareCountriesSchema);
        return (
            <FGStack style={{ height: '100%', width: '100%' }}>
                <PickerDrawer
                    title="Compare Countries"
                    side="right"
                    defaultOpen>
                    <CrossCountryRiskPicker />
                </PickerDrawer>
                <div
                    style={{
                        flex: 1,
                    }}>
                    {props.children}
                </div>
                <EventsFeedDataProvider />
            </FGStack>
        );
    }
);
